import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navigation } from '../components/Navigation';
import { LogOut, Users, Plus, FileText, Calendar, Check, Loader2, ChevronDown, ChevronRight, Sparkles, Copy, CheckCircle, Send, Bot, User as UserIcon, Save, X, ListTodo, Clock, AlertTriangle, Trash2, CalendarPlus, Pencil, MessageCircle, Archive, Pin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

// Deltagerlister per mødetype
const MEETING_CONFIGS = {
  ledelsen: {
    title: 'Ledelse',
    members: [
      { id: 'mika', name: 'Mika', email: 'mi@gardinbussen.org' },
      { id: 'claus', name: 'Claus', email: 'cl@gardinbussen.org' },
      { id: 'bo', name: 'Bo', email: 'bv@gardinbussen.org' },
      { id: 'mads', name: 'Mads', email: 'mop@gardinbussen.org' },
    ],
    apiPrefix: '/meetings/ledelsen',
  },
  marketing: {
    title: 'Marketing',
    members: [
      { id: 'mads', name: 'Mads', email: 'mop@gardinbussen.org' },
      { id: 'bo', name: 'Bo', email: 'bv@gardinbussen.org' },
      { id: 'hannah', name: 'Hannah', email: 'hl@gardinbussen.org' },
      { id: 'andreas', name: 'Andreas', email: 'ap@gardinbussen.org' },
    ],
    apiPrefix: '/meetings/marketing',
  },
  system: {
    title: 'System',
    members: [
      { id: 'bo', name: 'Bo', email: 'bv@gardinbussen.org' },
      { id: 'mads', name: 'Mads', email: 'mop@gardinbussen.org' },
      { id: 'mikael', name: 'Mikael', email: 'mr@gardinbussen.org' },
      { id: 'ronni', name: 'Ronni', email: 'ra@gardinbussen.org' },
    ],
    apiPrefix: '/meetings/system',
  },
  kundeservice: {
    title: 'Kundeservice',
    members: [
      { id: 'mads', name: 'Mads', email: 'mop@gardinbussen.org' },
      { id: 'bo', name: 'Bo', email: 'bv@gardinbussen.org' },
      { id: 'hannah', name: 'Hannah', email: 'hl@gardinbussen.org' },
    ],
    apiPrefix: '/meetings/kundeservice',
  },
  produkt: {
    title: 'Produkt',
    members: [
      { id: 'bo', name: 'Bo', email: 'bv@gardinbussen.org' },
      { id: 'mads', name: 'Mads', email: 'mop@gardinbussen.org' },
      { id: 'ronni', name: 'Ronni', email: 'ra@gardinshoppen.org' },
    ],
    apiPrefix: '/meetings/produkt',
  },
};

export default function MoederLedelsen({ user, meetingType = 'ledelsen' }) {
  const config = MEETING_CONFIGS[meetingType] || MEETING_CONFIGS.ledelsen;
  const TEAM_MEMBERS = config.members;
  const API_PREFIX = config.apiPrefix;
  const MEETING_TITLE = config.title;
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [agendaItems, setAgendaItems] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMeeting, setExpandedMeeting] = useState(null);
  const [activeTab, setActiveTab] = useState('prepare'); // 'prepare' | 'archive' | 'history'
  
  // Nyt dagsordenspunkt
  const [newAgendaItem, setNewAgendaItem] = useState('');
  const [addingAgenda, setAddingAgenda] = useState(false);
  
  // Møde state
  const [isInMeeting, setIsInMeeting] = useState(false);
  const [attendees, setAttendees] = useState(TEAM_MEMBERS.map(m => m.id));
  const [meetingNotes, setMeetingNotes] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showFinalSummary, setShowFinalSummary] = useState(false);
  const [finalSummary, setFinalSummary] = useState('');
  const [generatedAgenda, setGeneratedAgenda] = useState('');
  const [showAgendaPreview, setShowAgendaPreview] = useState(false);
  const [previewAgenda, setPreviewAgenda] = useState('');
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [editingAgendaId, setEditingAgendaId] = useState(null);
  const [editingAgendaText, setEditingAgendaText] = useState('');
  const [expandedActionId, setExpandedActionId] = useState(null);
  const [actionComments, setActionComments] = useState({});
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  
  // Faste punkter
  const [fixedPoints, setFixedPoints] = useState([]);
  const [newFixedPoint, setNewFixedPoint] = useState('');
  const [addingFixed, setAddingFixed] = useState(false);
  const [editingFixedId, setEditingFixedId] = useState(null);
  const [editingFixedText, setEditingFixedText] = useState('');

  // Preview af referat-udkast inden endelig generering
  const [showMeetingPreview, setShowMeetingPreview] = useState(false);

  // Sletning af gemt referat (kræver bekræftelse)
  const [deletingMeetingId, setDeletingMeetingId] = useState(null);
  const [isDeletingMeeting, setIsDeletingMeeting] = useState(false);

  // Punkter brugeren eksplicit har markeret som "Udskydes" i preview-modal
  const [postponedItems, setPostponedItems] = useState(new Set());
  
  // Email state
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailEmail, setGmailEmail] = useState('');
  const [emailRecipients, setEmailRecipients] = useState(TEAM_MEMBERS.map(m => m.email));
  const [customRecipients, setCustomRecipients] = useState('');
  const [sendingEmail, setSendingEmail] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(null);
  const [emailSchedules, setEmailSchedules] = useState([]);
  const [showEmailSection, setShowEmailSection] = useState(false);
  
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll til bunden når der kommer nye beskeder
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [meetingNotes]);

  // Hent data
  const fetchData = useCallback(async () => {
    try {
      // Reset state when changing meeting type, so stale data doesn't flash
      setMeetings([]);
      setAgendaItems([]);
      setActionItems([]);
      setFixedPoints([]);
      // Fetch each independently so one failure doesn't block others
      const [meetingsRes, agendaRes, actionsRes, fixedRes] = await Promise.allSettled([
        api.get(`${API_PREFIX}`),
        api.get(`${API_PREFIX}/agenda`),
        api.get(`${API_PREFIX}/actions`),
        api.get(`${API_PREFIX}/fixed-points`)
      ]);
      if (meetingsRes.status === 'fulfilled') setMeetings(meetingsRes.value.data.meetings || []);
      if (agendaRes.status === 'fulfilled') setAgendaItems(agendaRes.value.data.items || []);
      if (actionsRes.status === 'fulfilled') setActionItems(actionsRes.value.data.items || []);
      if (fixedRes.status === 'fulfilled') setFixedPoints(fixedRes.value.data.items || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [API_PREFIX]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check Gmail connection and fetch email schedules
  useEffect(() => {
    const checkGmail = async () => {
      try {
        const [statusRes, schedulesRes] = await Promise.all([
          api.get('/oauth/gmail/status'),
          api.get(`${API_PREFIX}/email-schedules`)
        ]);
        setGmailConnected(statusRes.data.connected);
        setGmailEmail(statusRes.data.email || '');
        setEmailSchedules(schedulesRes.data.schedules || []);
      } catch {}
    };
    
    // Check URL params for Gmail callback FIRST
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail_connected')) {
      setGmailConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('gmail_error')) {
      alert('Gmail-fejl: ' + decodeURIComponent(params.get('gmail_error')));
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Then verify with backend
    checkGmail();
  }, [API_PREFIX]);

  const connectGmail = async () => {
    try {
      const res = await api.get('/oauth/gmail/auth-url');
      window.location.href = res.data.auth_url;
    } catch (err) {
      console.error('Gmail auth error:', err);
    }
  };

  const getAllRecipients = () => {
    const selected = emailRecipients;
    const custom = customRecipients.split(',').map(e => e.trim()).filter(e => e.includes('@'));
    return [...new Set([...selected, ...custom])];
  };

  const toggleEmailRecipient = (email) => {
    setEmailRecipients(prev => 
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  const sendEmail = async (type) => {
    const recipients = getAllRecipients();
    if (!recipients.length) return;
    setSendingEmail(type);
    setEmailSuccess(null);
    try {
      await api.post(`${API_PREFIX}/send-${type === 'suggestion_invite' ? 'suggestion-invite' : type}`, { recipients });
      setEmailSuccess(type);
      setTimeout(() => setEmailSuccess(null), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Fejl ved afsendelse');
    } finally {
      setSendingEmail(null);
    }
  };

  const saveSchedule = async (type, weekday, hour, minute) => {
    try {
      const recipients = getAllRecipients();
      await api.post(`${API_PREFIX}/email-schedules`, { type, weekday, hour, minute, recipients, active: true });
      const res = await api.get(`${API_PREFIX}/email-schedules`);
      setEmailSchedules(res.data.schedules || []);
    } catch (err) {
      alert(err.response?.data?.detail || 'Fejl');
    }
  };

  const deleteSchedule = async (type) => {
    try {
      await api.delete(`${API_PREFIX}/email-schedules/${type}`);
      setEmailSchedules(prev => prev.filter(s => s.type !== type));
    } catch {}
  };

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    navigate('/login');
  };

  const toggleAttendee = (id) => {
    setAttendees(prev => 
      prev.includes(id) 
        ? prev.filter(a => a !== id)
        : [...prev, id]
    );
  };

  // Tilføj dagsordenspunkt
  const addAgendaItem = async () => {
    if (!newAgendaItem.trim()) return;
    setAddingAgenda(true);
    try {
      await api.post(`${API_PREFIX}/agenda`, { content: newAgendaItem.trim() });
      setNewAgendaItem('');
      await fetchData();
    } catch (err) {
      console.error('Error adding agenda item:', err);
      alert('Fejl ved tilføjelse: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAddingAgenda(false);
    }
  };

  const deleteAgendaItem = async (id) => {
    try {
      await api.delete(`${API_PREFIX}/agenda/${id}`);
      fetchData();
    } catch (err) {
      console.error('Error deleting agenda item:', err);
    }
  };

  const startEditAgenda = (item) => {
    setEditingAgendaId(item.id);
    setEditingAgendaText(item.content);
  };

  const cancelEditAgenda = () => {
    setEditingAgendaId(null);
    setEditingAgendaText('');
  };

  const saveEditAgenda = async () => {
    if (!editingAgendaText.trim()) return;
    try {
      await api.patch(`${API_PREFIX}/agenda/${editingAgendaId}`, { content: editingAgendaText.trim() });
      setEditingAgendaId(null);
      setEditingAgendaText('');
      fetchData();
    } catch (err) {
      console.error('Error updating agenda item:', err);
    }
  };

  const toggleActionComplete = async (id, currentStatus) => {
    try {
      await api.patch(`${API_PREFIX}/actions/${id}`, { completed: !currentStatus });
      fetchData();
    } catch (err) {
      console.error('Error updating action:', err);
    }
  };

  // Kommentar funktioner
  const toggleActionExpand = async (actionId) => {
    if (expandedActionId === actionId) {
      setExpandedActionId(null);
      return;
    }
    
    setExpandedActionId(actionId);
    setLoadingComments(true);
    
    try {
      const response = await api.get(`${API_PREFIX}/actions/${actionId}/comments`);
      setActionComments(prev => ({
        ...prev,
        [actionId]: response.data.comments || []
      }));
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const addComment = async (actionId) => {
    if (!newComment.trim()) return;
    
    try {
      const response = await api.post(`${API_PREFIX}/actions/${actionId}/comments`, {
        content: newComment.trim()
      });
      
      setActionComments(prev => ({
        ...prev,
        [actionId]: [...(prev[actionId] || []), {
          id: response.data.id,
          content: newComment.trim(),
          author: response.data.author,
          created_at: response.data.created_at
        }]
      }));
      setNewComment('');
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  };

  const deleteComment = async (actionId, commentId) => {
    try {
      await api.delete(`${API_PREFIX}/actions/${actionId}/comments/${commentId}`);
      setActionComments(prev => ({
        ...prev,
        [actionId]: (prev[actionId] || []).filter(c => c.id !== commentId)
      }));
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  // Faste punkter CRUD
  const addFixedPoint = async () => {
    if (!newFixedPoint.trim()) return;
    setAddingFixed(true);
    try {
      await api.post(`${API_PREFIX}/fixed-points`, { content: newFixedPoint.trim() });
      setNewFixedPoint('');
      await fetchData();
    } catch (err) {
      console.error('Error adding fixed point:', err);
      alert('Fejl ved tilføjelse: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAddingFixed(false);
    }
  };

  const deleteFixedPoint = async (id) => {
    try {
      await api.delete(`${API_PREFIX}/fixed-points/${id}`);
      fetchData();
    } catch (err) {
      console.error('Error deleting fixed point:', err);
    }
  };

  const startEditFixed = (item) => {
    setEditingFixedId(item.id);
    setEditingFixedText(item.content);
  };

  const cancelEditFixed = () => {
    setEditingFixedId(null);
    setEditingFixedText('');
  };

  const saveEditFixed = async () => {
    if (!editingFixedText.trim()) return;
    try {
      await api.patch(`${API_PREFIX}/fixed-points/${editingFixedId}`, { content: editingFixedText.trim() });
      setEditingFixedId(null);
      setEditingFixedText('');
      fetchData();
    } catch (err) {
      console.error('Error updating fixed point:', err);
    }
  };

  // Forhåndsvis dagsorden
  const previewAgendaHandler = async () => {
    const attendeeNames = TEAM_MEMBERS
      .filter(m => attendees.includes(m.id))
      .map(m => m.name);
    
    setIsGeneratingPreview(true);
    setShowAgendaPreview(true);
    setPreviewAgenda('');
    
    try {
      const response = await api.post(`${API_PREFIX}/generate-agenda`, {
        agenda_items: agendaItems.map(a => a.content),
        action_items: actionItems.filter(a => !a.completed),
        attendees: attendeeNames
      });
      
      setPreviewAgenda(response.data.agenda);
    } catch (err) {
      console.error('Error generating preview:', err);
      setPreviewAgenda('Kunne ikke generere dagsorden. Prøv igen.');
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // Start møde med AI-genereret dagsorden
  const startMeeting = async () => {
    const attendeeNames = TEAM_MEMBERS
      .filter(m => attendees.includes(m.id))
      .map(m => m.name);
    
    setIsInMeeting(true);
    setIsAiThinking(true);
    
    // Initial besked
    setMeetingNotes([{
      type: 'ai',
      content: `Forbereder mødet...`
    }]);
    
    try {
      // Generér dagsorden baseret på forslag og handlingspunkter
      const response = await api.post(`${API_PREFIX}/generate-agenda`, {
        agenda_items: agendaItems.map(a => a.content),
        action_items: actionItems.filter(a => !a.completed),
        attendees: attendeeNames
      });
      
      const agenda = response.data.agenda;
      setGeneratedAgenda(agenda);

      const welcomeMsg = `Velkommen til ledelsesmødet! 👋\n\nDeltagere: ${attendeeNames.join(', ')}\n\n${agenda}\n\n---\n\nJeg starter med at gennemgå alle faste punkter og udestående handlingspunkter — ét ad gangen.`;

      setMeetingNotes([{ type: 'ai', content: welcomeMsg }]);

      // Trigger AI review-flow: send et internt kickoff-besked så AI'en starter med at
      // gennemgå faste punkter + handlingspunkter ét ad gangen
      try {
        const kickoffResponse = await api.post('/meetings/ai-assist', {
          current_message: '[KICKOFF] Mødet er nu startet. Begynd opstartsflow ved at gennemgå første faste punkt eller første handlingspunkt.',
          meeting_context: welcomeMsg,
          attendees: attendeeNames,
          pending_actions: actionItems.filter(a => !a.completed),
          agenda_items: agendaItems.map(a => a.content),
          fixed_points: fixedPoints.map(f => f.content)
        });
        if (kickoffResponse.data.response) {
          setMeetingNotes(prev => [...prev, { type: 'ai', content: kickoffResponse.data.response }]);
        }
      } catch (kickErr) {
        console.error('Kunne ikke starte review-flow:', kickErr);
      }
    } catch (err) {
      console.error('Error generating agenda:', err);
      setMeetingNotes([{
        type: 'ai',
        content: `Velkommen til ledelsesmødet! 👋\n\nDeltagere: ${attendeeNames.join(', ')}\n\nJeg følger med under mødet og hjælper jer med at:\n• Sikre at beslutninger har en ansvarlig\n• Minde jer om deadlines\n• Stille opfølgende spørgsmål\n\nSkriv løs!`
      }]);
    } finally {
      setIsAiThinking(false);
    }
  };

  const sendMessage = async () => {
    if (!currentInput.trim() || isAiThinking) return;
    
    const userMessage = currentInput.trim();
    setCurrentInput('');
    
    setMeetingNotes(prev => [...prev, { type: 'user', content: userMessage }]);
    setIsAiThinking(true);
    
    try {
      const attendeeNames = TEAM_MEMBERS
        .filter(m => attendees.includes(m.id))
        .map(m => m.name);
      
      // Byg fuld samtalehistorik inkl. AI-svar
      const fullContext = meetingNotes
        .map(n => n.type === 'user' ? `BRUGER: ${n.content}` : `AI: ${n.content}`)
        .join('\n\n');
      
      const response = await api.post('/meetings/ai-assist', {
        current_message: userMessage,
        meeting_context: fullContext,
        attendees: attendeeNames,
        pending_actions: actionItems.filter(a => !a.completed),
        agenda_items: agendaItems.map(a => a.content),
        fixed_points: fixedPoints.map(f => f.content)
      });
      
      if (response.data.response) {
        setMeetingNotes(prev => [...prev, { type: 'ai', content: response.data.response }]);
      }
      
      // Hvis AI'en har arkiveret handlingspunkter, opdater data
      if (response.data.completed_action_ids && response.data.completed_action_ids.length > 0) {
        fetchData();
      }
    } catch (err) {
      console.error('AI assist error:', err);
    } finally {
      setIsAiThinking(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Detect items that have NOT been mentioned in any user note during the meeting.
  // Strategy: For each item, take all words >=4 chars (excl. common stop words), and require
  // at least 30% to appear (case-insensitive substring) in the combined meeting notes.
  const findUndiscussedItems = (items) => {
    if (!items || items.length === 0) return [];
    const STOP = new Set(['skal', 'have', 'vores', 'mellem', 'efter', 'inden', 'omkring', 'andre', 'noget', 'meget', 'derfor', 'kunne', 'ville', 'blevet', 'bliver', 'gerne', 'punkt', 'dagsorden']);
    const allNotesLower = meetingNotes
      .filter(n => n.type === 'user' || n.type === 'ai')
      .map(n => (n.content || '').toLowerCase())
      .join(' ');
    return items.filter(item => {
      const text = (item.content || '').toLowerCase();
      const words = text.split(/[^a-zæøå0-9]+/i).filter(w => w.length >= 4 && !STOP.has(w));
      if (words.length === 0) return false;
      const hits = words.filter(w => allNotesLower.includes(w)).length;
      return (hits / words.length) < 0.3;
    });
  };

  const undiscussedAgenda = findUndiscussedItems(agendaItems);
  const undiscussedFixed = findUndiscussedItems(fixedPoints);

  const deleteMeeting = async (meetingId) => {
    setIsDeletingMeeting(true);
    try {
      await api.delete(`${API_PREFIX}/${meetingId}`);
      setDeletingMeetingId(null);
      await fetchData();
    } catch (err) {
      console.error('Kunne ikke slette referat:', err);
      alert('Kunne ikke slette referatet. Prøv igen.');
    } finally {
      setIsDeletingMeeting(false);
    }
  };

  const generateFinalSummary = async () => {
    setIsAiThinking(true);
    setShowMeetingPreview(false);

    try {
      const attendeeNames = TEAM_MEMBERS
        .filter(m => attendees.includes(m.id))
        .map(m => m.name);
      
      const allNotes = meetingNotes
        .filter(n => n.type === 'user')
        .map(n => n.content)
        .join('\n\n');
      
      const response = await api.post('/meetings/generate-summary', {
        notes: allNotes,
        attendees: attendeeNames,
        meeting_type: 'Ledelsesmøde',
        agenda_items: agendaItems.map(a => a.content),
        postponed_items: Array.from(postponedItems)
      });
      
      setFinalSummary(response.data.summary);
      setShowFinalSummary(true);
      
      // Hvis der er udskudte punkter, tilføj dem til næste mødes dagsorden
      if (response.data.deferred_items && response.data.deferred_items.length > 0) {
        for (const item of response.data.deferred_items) {
          await api.post(`${API_PREFIX}/agenda`, { content: `[Udskudt] ${item}` });
        }
      }
      
      // Markér afsluttede handlingspunkter som completed
      if (response.data.completed_items && response.data.completed_items.length > 0) {
        for (const completedDesc of response.data.completed_items) {
          // Find matching action item
          const matchingAction = actionItems.find(a => 
            !a.completed && a.description.toLowerCase().includes(completedDesc.toLowerCase().substring(0, 20))
          );
          if (matchingAction) {
            await api.patch(`${API_PREFIX}/actions/${matchingAction.id}`, { completed: true });
          }
        }
      }
    } catch (err) {
      console.error('Error generating summary:', err);
      alert('Kunne ikke generere referat. Prøv igen.');
    } finally {
      setIsAiThinking(false);
    }
  };

  const saveMeeting = async () => {
    if (!finalSummary.trim()) return;
    
    setIsSaving(true);
    try {
      const attendeeData = TEAM_MEMBERS
        .filter(m => attendees.includes(m.id))
        .map(m => ({ name: m.name, email: m.email }));
      
      const allNotes = meetingNotes
        .filter(n => n.type === 'user')
        .map(n => n.content)
        .join('\n\n');
      
      // Gem møde og udtræk handlingspunkter
      await api.post(`${API_PREFIX}`, {
        notes: allNotes,
        summary: finalSummary,
        attendees: attendeeData,
        extract_actions: true  // Bed backend om at udtrække handlingspunkter
      });
      
      // Slet brugte dagsordenspunkter
      for (const item of agendaItems) {
        await api.delete(`${API_PREFIX}/agenda/${item.id}`);
      }
      
      // Send referat automatisk til tilstedeværende
      try {
        const attendeeEmails = TEAM_MEMBERS
          .filter(m => attendees.includes(m.id))
          .map(m => m.email);
        
        if (attendeeEmails.length > 0) {
          await api.post(`${API_PREFIX}/send-summary`, {
            recipients: attendeeEmails
          });
          console.log('Referat sendt til:', attendeeEmails);
        }
      } catch (emailErr) {
        console.warn('Kunne ikke sende referat automatisk:', emailErr);
        // Vis ikke fejl - mødet er gemt, email er sekundært
      }
      
      // Reset
      setIsInMeeting(false);
      setMeetingNotes([]);
      setFinalSummary('');
      setShowFinalSummary(false);
      setGeneratedAgenda('');
      setAttendees(TEAM_MEMBERS.map(m => m.id));
      setPostponedItems(new Set());
      fetchData();
    } catch (err) {
      console.error('Error saving meeting:', err);
      alert('Kunne ikke gemme mødet. Prøv igen.');
    } finally {
      setIsSaving(false);
    }
  };

  const endMeetingWithoutSaving = () => {
    if (meetingNotes.filter(n => n.type === 'user').length > 0) {
      if (!confirm('Er du sikker? Mødenoterne vil gå tabt.')) return;
    }
    setIsInMeeting(false);
    setMeetingNotes([]);
    setFinalSummary('');
    setShowFinalSummary(false);
    setGeneratedAgenda('');
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('da-DK', { 
      weekday: 'long',
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDeadline = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { text: `${Math.abs(diffDays)} dage overskredet`, color: 'text-red-500', urgent: true };
    if (diffDays === 0) return { text: 'I dag', color: 'text-orange-500', urgent: true };
    if (diffDays <= 7) return { text: `Om ${diffDays} dage`, color: 'text-yellow-500', urgent: false };
    return { text: date.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' }), color: 'text-white/50', urgent: false };
  };

  // Tæl ventende og overdue actions
  const overdueActions = actionItems.filter(a => !a.completed && a.deadline && new Date(a.deadline) < new Date());
  const upcomingActions = actionItems.filter(a => !a.completed && a.deadline && new Date(a.deadline) >= new Date());

  return (
    <div className="min-h-screen bg-[#121212] flex flex-col" data-testid="moeder-ledelsen">
      {/* Header */}
      <header className="bg-[#0a0a0a] border-b border-white/10 px-3 sm:px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-4">
          <Navigation />
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#1E6F7A]" />
            <h1 className="text-base sm:text-xl font-bold text-white">{MEETING_TITLE}møder</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-4">
          {isInMeeting && (
            <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-green-500 text-sm font-medium hidden sm:block">Møde i gang</span>
            </div>
          )}
          {overdueActions.length > 0 && !isInMeeting && (
            <div className="flex items-center gap-1 px-2 py-1 bg-red-500/20 rounded-full">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-red-500 text-xs font-medium">{overdueActions.length}</span>
            </div>
          )}
          <button onClick={handleLogout} className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
            <LogOut className="w-4 h-4 text-white/60" />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-3 sm:p-6 overflow-hidden">
        <div className="max-w-5xl mx-auto h-full flex flex-col gap-4">
          
          {!isInMeeting ? (
            <>
              {/* Tabs */}
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab('prepare')}
                  data-testid="tab-prepare"
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'prepare' 
                      ? 'bg-[#1E6F7A] text-white' 
                      : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
                >
                  <CalendarPlus className="w-4 h-4 inline mr-2" />
                  Forbered møde
                </button>
                <button
                  onClick={() => setActiveTab('archive')}
                  data-testid="tab-archive"
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'archive' 
                      ? 'bg-[#1E6F7A] text-white' 
                      : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
                >
                  <Archive className="w-4 h-4 inline mr-2" />
                  Arkiv
                  {actionItems.filter(a => a.completed).length > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-white/20 rounded-full text-[10px]">
                      {actionItems.filter(a => a.completed).length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  data-testid="tab-history"
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'history' 
                      ? 'bg-[#1E6F7A] text-white' 
                      : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
                >
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Tidligere møder
                </button>
              </div>

              {activeTab === 'prepare' ? (
                <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 overflow-hidden">
                  {/* Venstre kolonne: Dagsordenspunkter */}
                  <div className="flex flex-col gap-4 overflow-hidden">
                    {/* Tilføj dagsordenspunkt */}
                    <section className="bg-[#161616] rounded-2xl p-4 border border-white/5">
                      <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
                        <ListTodo className="w-4 h-4 text-[#1E6F7A]" />
                        Tilføj til dagsorden
                      </h3>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newAgendaItem}
                          onChange={(e) => setNewAgendaItem(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addAgendaItem()}
                          placeholder="Skriv et emne til næste møde..."
                          className="flex-1 bg-[#252525] border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#1E6F7A]"
                          data-testid="agenda-input"
                        />
                        <button
                          onClick={addAgendaItem}
                          disabled={!newAgendaItem.trim() || addingAgenda}
                          className="px-3 py-2 bg-[#1E6F7A] hover:bg-[#257a86] disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                        >
                          {addingAgenda ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        </button>
                      </div>
                    </section>

                    {/* Dagsordenspunkter liste */}
                    <section className="flex-1 bg-[#161616] rounded-2xl p-4 border border-white/5 overflow-hidden flex flex-col">
                      <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
                        <FileText className="w-4 h-4 text-[#1E6F7A]" />
                        Dagsordensforslag ({agendaItems.length})
                      </h3>
                      {agendaItems.length === 0 ? (
                        <p className="text-white/40 text-sm text-center py-4">
                          Ingen punkter endnu. Tilføj emner i løbet af ugen.
                        </p>
                      ) : (
                        <ul className="space-y-2 overflow-y-auto flex-1">
                          {agendaItems.map((item) => (
                            <li key={item.id} className="flex items-start gap-2 p-2 bg-[#252525] rounded-lg group">
                              {editingAgendaId === item.id ? (
                                /* Redigerings-tilstand */
                                <div className="flex-1 flex gap-2">
                                  <input
                                    type="text"
                                    value={editingAgendaText}
                                    onChange={(e) => setEditingAgendaText(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && saveEditAgenda()}
                                    className="flex-1 bg-[#1a1a1a] border border-[#1E6F7A] rounded px-2 py-1 text-white text-sm focus:outline-none"
                                    autoFocus
                                  />
                                  <button
                                    onClick={saveEditAgenda}
                                    className="p-1 text-green-500 hover:text-green-400"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={cancelEditAgenda}
                                    className="p-1 text-white/50 hover:text-white"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                /* Normal visning */
                                <>
                                  <div className="w-2 h-2 bg-[#1E6F7A] rounded-full mt-1.5 flex-shrink-0" />
                                  <span className="text-white/80 text-sm flex-1">{item.content}</span>
                                  <button
                                    onClick={() => startEditAgenda(item)}
                                    className="p-1 text-white/30 hover:text-[#1E6F7A] opacity-0 group-hover:opacity-100 transition-all"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteAgendaItem(item.id)}
                                    className="p-1 text-white/30 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  </div>

                  {/* Højre kolonne: Faste punkter + Handlingspunkter + Start møde */}
                  <div className="flex flex-col gap-4 overflow-hidden">
                    {/* Faste punkter */}
                    <section className="bg-[#161616] rounded-2xl p-4 border border-white/5" data-testid="fixed-points-section">
                      <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
                        <Pin className="w-4 h-4 text-[#1E6F7A]" />
                        Faste punkter ({fixedPoints.length})
                      </h3>
                      
                      {fixedPoints.length > 0 && (
                        <ul className="space-y-2 mb-3">
                          {fixedPoints.map((item) => (
                            <li key={item.id} className="flex items-start gap-2 p-2 bg-[#252525] rounded-lg group" data-testid={`fixed-point-${item.id}`}>
                              {editingFixedId === item.id ? (
                                <div className="flex-1 flex gap-2">
                                  <input
                                    type="text"
                                    value={editingFixedText}
                                    onChange={(e) => setEditingFixedText(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && saveEditFixed()}
                                    className="flex-1 bg-[#1a1a1a] border border-[#1E6F7A] rounded px-2 py-1 text-white text-sm focus:outline-none"
                                    autoFocus
                                  />
                                  <button onClick={saveEditFixed} className="p-1 text-green-500 hover:text-green-400">
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button onClick={cancelEditFixed} className="p-1 text-white/50 hover:text-white">
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <Pin className="w-3 h-3 text-[#1E6F7A] mt-1 flex-shrink-0" />
                                  <span className="text-white/80 text-sm flex-1">{item.content}</span>
                                  <button
                                    onClick={() => startEditFixed(item)}
                                    className="p-1 text-white/30 hover:text-[#1E6F7A] opacity-0 group-hover:opacity-100 transition-all"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => deleteFixedPoint(item.id)}
                                    className="p-1 text-white/30 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newFixedPoint}
                          onChange={(e) => setNewFixedPoint(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addFixedPoint()}
                          placeholder="Tilføj fast punkt..."
                          className="flex-1 bg-[#252525] border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#1E6F7A]"
                          data-testid="fixed-point-input"
                        />
                        <button
                          onClick={addFixedPoint}
                          disabled={!newFixedPoint.trim() || addingFixed}
                          className="px-3 py-2 bg-[#1E6F7A] hover:bg-[#257a86] disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                          data-testid="fixed-point-add-btn"
                        >
                          {addingFixed ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        </button>
                      </div>
                    </section>

                    {/* Handlingspunkter */}
                    <section className="flex-1 bg-[#161616] rounded-2xl p-4 border border-white/5 overflow-hidden flex flex-col">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                          <Clock className="w-4 h-4 text-[#1E6F7A]" />
                          Handlingspunkter ({actionItems.filter(a => !a.completed).length} åbne)
                        </h3>
                        {actionItems.length > 0 && (
                          <button
                            onClick={async () => {
                              if (window.confirm('Er du sikker på at du vil slette ALLE handlingspunkter? Dette kan ikke fortrydes.')) {
                                if (window.confirm('Sidste advarsel: Alle handlingspunkter slettes permanent. Fortsæt?')) {
                                  try {
                                    await api.delete(`${API_PREFIX}/actions/clear-all`);
                                    await fetchData();
                                  } catch (err) {
                                    alert('Fejl: ' + (err.response?.data?.detail || err.message));
                                  }
                                }
                              }
                            }}
                            className="p-1.5 text-white/20 hover:text-red-500 transition-colors"
                            title="Slet alle handlingspunkter"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {actionItems.filter(a => !a.completed).length === 0 ? (
                        <p className="text-white/40 text-sm text-center py-4">
                          Ingen åbne handlingspunkter
                        </p>
                      ) : (
                        <ul className="space-y-2 overflow-y-auto flex-1">
                          {actionItems.filter(a => !a.completed).map((item) => {
                            const deadline = formatDeadline(item.deadline);
                            const comments = actionComments[item.id] || [];
                            const isExpanded = expandedActionId === item.id;
                            
                            return (
                              <li key={item.id} className={`rounded-lg border ${deadline?.urgent ? 'bg-red-500/10 border-red-500/30' : 'bg-[#252525] border-white/5'}`}>
                                <div className="p-3">
                                  <div className="flex items-start gap-2">
                                    <div className="w-5 h-5 rounded border border-white/20 flex items-center justify-center flex-shrink-0 mt-0.5 bg-white/5">
                                      <div className="w-2 h-2 rounded-full bg-[#1E6F7A]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-white/90 text-sm">{item.description}</p>
                                      <div className="flex items-center gap-3 mt-1 text-xs">
                                        <span className="text-white/50">{item.responsible}</span>
                                        {deadline && (
                                          <span className={deadline.color}>
                                            {deadline.urgent && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                                            {deadline.text}
                                          </span>
                                        )}
                                        <button
                                          onClick={() => toggleActionExpand(item.id)}
                                          className="flex items-center gap-1 text-white/40 hover:text-[#1E6F7A] transition-colors"
                                        >
                                          <MessageCircle className="w-3 h-3" />
                                          <span>{comments.length || ''}</span>
                                        </button>
                                        <button
                                          onClick={async () => {
                                            if (window.confirm(`Slet handlingspunkt "${item.description}"?`)) {
                                              try {
                                                await api.delete(`${API_PREFIX}/actions/${item.id}`);
                                                await fetchData();
                                              } catch (err) {
                                                alert('Fejl: ' + (err.response?.data?.detail || err.message));
                                              }
                                            }
                                          }}
                                          className="text-white/20 hover:text-red-500 transition-colors"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      </div>
                                      <p className="text-[10px] text-white/30 mt-1 italic">Afsluttes ved møde</p>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Kommentar-tråd */}
                                {isExpanded && (
                                  <div className="border-t border-white/10 p-3 bg-[#1a1a1a] rounded-b-lg">
                                    {loadingComments ? (
                                      <div className="flex justify-center py-2">
                                        <Loader2 className="w-4 h-4 animate-spin text-white/40" />
                                      </div>
                                    ) : (
                                      <>
                                        {comments.length > 0 && (
                                          <div className="space-y-2 mb-3 max-h-32 overflow-y-auto">
                                            {comments.map((comment) => (
                                              <div key={comment.id} className="flex gap-2 group">
                                                <div className="w-6 h-6 rounded-full bg-[#1E6F7A]/30 flex items-center justify-center flex-shrink-0">
                                                  <span className="text-[10px] text-[#1E6F7A] font-medium">
                                                    {comment.author?.charAt(0).toUpperCase()}
                                                  </span>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="flex items-center gap-2">
                                                    <span className="text-xs text-white/70 font-medium">{comment.author}</span>
                                                    <span className="text-[10px] text-white/30">
                                                      {new Date(comment.created_at).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                    <button
                                                      onClick={() => deleteComment(item.id, comment.id)}
                                                      className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-500 transition-all"
                                                    >
                                                      <X className="w-3 h-3" />
                                                    </button>
                                                  </div>
                                                  <p className="text-xs text-white/60">{comment.content}</p>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        
                                        <div className="flex gap-2">
                                          <input
                                            type="text"
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && addComment(item.id)}
                                            placeholder="Skriv en kommentar..."
                                            className="flex-1 bg-[#252525] border border-white/10 rounded px-2 py-1.5 text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#1E6F7A]"
                                          />
                                          <button
                                            onClick={() => addComment(item.id)}
                                            disabled={!newComment.trim()}
                                            className="px-2 py-1.5 bg-[#1E6F7A] hover:bg-[#257a86] disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                                          >
                                            <Send className="w-3 h-3" />
                                          </button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </section>

                    {/* Start møde */}
                    <section className="bg-[#161616] rounded-2xl p-4 border border-white/5">
                      <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
                        <Sparkles className="w-4 h-4 text-[#1E6F7A]" />
                        Start møde
                      </h3>
                      
                      <div className="mb-3">
                        <label className="block text-xs text-white/50 mb-2">Hvem deltager?</label>
                        <div className="flex flex-wrap gap-2">
                          {TEAM_MEMBERS.map((member) => (
                            <button
                              key={member.id}
                              onClick={() => toggleAttendee(member.id)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                                attendees.includes(member.id)
                                  ? 'bg-[#1E6F7A] text-white'
                                  : 'bg-white/5 text-white/50 hover:bg-white/10'
                              }`}
                            >
                              {attendees.includes(member.id) && <Check className="w-3 h-3" />}
                              {member.name}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex gap-2 mb-2">
                        <button
                          onClick={previewAgendaHandler}
                          disabled={attendees.length === 0 || (agendaItems.length === 0 && actionItems.filter(a => !a.completed).length === 0)}
                          className="flex-1 bg-white/5 hover:bg-white/10 disabled:bg-white/5 disabled:text-white/30 disabled:cursor-not-allowed text-white/70 rounded-xl p-3 flex items-center justify-center gap-2 transition-all font-medium text-sm border border-white/10"
                          data-testid="preview-agenda-btn"
                        >
                          <FileText className="w-4 h-4" />
                          Forhåndsvis dagsorden
                        </button>
                      </div>
                      
                      <button
                        onClick={startMeeting}
                        disabled={attendees.length === 0}
                        className="w-full bg-gradient-to-r from-[#1E6F7A] to-[#2a8a97] hover:from-[#257a86] hover:to-[#319aa8] disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-xl p-3 flex items-center justify-center gap-2 transition-all font-medium text-sm"
                        data-testid="start-meeting-btn"
                      >
                        <Sparkles className="w-4 h-4" />
                        Start møde med AI-assistent
                      </button>
                      
                      {(agendaItems.length > 0 || overdueActions.length > 0) && (
                        <p className="text-xs text-white/40 text-center mt-2">
                          AI genererer dagsorden fra {agendaItems.length} emner
                          {overdueActions.length > 0 && ` + ${overdueActions.length} forfaldne handlinger`}
                        </p>
                      )}
                    </section>

                    {/* Email-funktioner */}
                    <section className="bg-[#161616] rounded-2xl border border-white/5 overflow-hidden">
                      <button
                        onClick={() => setShowEmailSection(!showEmailSection)}
                        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                        data-testid="email-section-toggle"
                      >
                        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                          <Send className="w-4 h-4 text-[#1E6F7A]" />
                          Email-funktioner
                        </h3>
                        <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${showEmailSection ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {showEmailSection && (
                        <div className="p-4 pt-0 space-y-4">
                          {/* Gmail Connection */}
                          <div className="flex items-center justify-between bg-white/5 rounded-xl p-3">
                            <div>
                              <p className="text-white text-sm font-medium">Gmail</p>
                              <p className="text-xs text-gray-400">
                                {gmailConnected ? `Forbundet: ${gmailEmail}` : 'Ikke forbundet'}
                              </p>
                            </div>
                            <button
                              onClick={connectGmail}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                gmailConnected 
                                  ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                                  : 'bg-[#1E6F7A] text-white hover:bg-[#1E6F7A]/80'
                              }`}
                              data-testid="connect-gmail-btn"
                            >
                              {gmailConnected ? 'Genforbind' : 'Forbind Gmail'}
                            </button>
                          </div>

                          {/* Recipients */}
                          <div>
                            <p className="text-white text-sm font-medium mb-2">Modtagere</p>
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {TEAM_MEMBERS.map(m => (
                                <button
                                  key={m.id}
                                  onClick={() => toggleEmailRecipient(m.email)}
                                  className={`px-2 py-1 rounded-lg text-xs transition-colors ${
                                    emailRecipients.includes(m.email)
                                      ? 'bg-[#1E6F7A] text-white'
                                      : 'bg-white/5 text-white/50 hover:bg-white/10'
                                  }`}
                                >
                                  {m.name}
                                </button>
                              ))}
                            </div>
                            <input
                              type="text"
                              value={customRecipients}
                              onChange={(e) => setCustomRecipients(e.target.value)}
                              placeholder="Ekstra emails (kommasepareret)"
                              className="w-full bg-[#0B0C10] border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs placeholder-gray-500 focus:border-[#1E6F7A] focus:outline-none"
                            />
                          </div>

                          {/* Send Buttons */}
                          <div className="space-y-2">
                            {[
                              { type: 'suggestion_invite', label: 'Bed om dagsordenspunkter' },
                              { type: 'agenda', label: 'Send dagsorden' },
                            ].map(({ type, label }) => (
                              <button
                                key={type}
                                onClick={() => sendEmail(type)}
                                disabled={!gmailConnected || sendingEmail === type}
                                className="w-full bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed border border-white/10 rounded-lg p-2.5 text-left transition-colors flex items-center justify-between"
                                data-testid={`send-${type}-btn`}
                              >
                                <span className="text-white text-sm">{label}</span>
                                {sendingEmail === type && <Loader2 className="w-3 h-3 animate-spin text-[#1E6F7A]" />}
                                {emailSuccess === type && <CheckCircle className="w-3 h-3 text-green-400" />}
                              </button>
                            ))}
                          </div>

                          {/* Schedules */}
                          <div>
                            <p className="text-white text-xs font-medium mb-2">Auto-udsendelse</p>
                            <div className="space-y-1.5">
                              {[
                                { type: 'suggestion_invite', label: 'Invitation' },
                                { type: 'agenda', label: 'Dagsorden' },
                              ].map(({ type, label }) => {
                                const existing = emailSchedules.find(s => s.type === type);
                                const days = ['Man', 'Tir', 'Ons', 'Tor', 'Fre'];
                                return (
                                  <div key={type} className="bg-white/5 rounded-lg p-2 flex items-center gap-2 flex-wrap">
                                    <span className="text-white text-xs font-medium w-20">{label}</span>
                                    <select
                                      defaultValue={existing?.weekday ?? 0}
                                      onChange={(e) => {
                                        const wd = parseInt(e.target.value);
                                        saveSchedule(type, wd, existing?.hour ?? 9, existing?.minute ?? 0);
                                      }}
                                      className="bg-[#0B0C10] border border-white/10 rounded px-1.5 py-0.5 text-white text-xs"
                                    >
                                      {days.map((d, i) => <option key={i} value={i}>{d}</option>)}
                                    </select>
                                    <input
                                      type="time"
                                      defaultValue={`${String(existing?.hour ?? 9).padStart(2,'0')}:${String(existing?.minute ?? 0).padStart(2,'0')}`}
                                      onChange={(e) => {
                                        const [h, m] = e.target.value.split(':').map(Number);
                                        saveSchedule(type, existing?.weekday ?? 0, h, m);
                                      }}
                                      className="bg-[#0B0C10] border border-white/10 rounded px-1.5 py-0.5 text-white text-xs"
                                    />
                                    {existing && (
                                      <button onClick={() => deleteSchedule(type)} className="text-red-400 hover:text-red-300 p-0.5">
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </section>
                  </div>
                </div>
              ) : activeTab === 'archive' ? (
                /* Arkiv tab */
                <section className="flex-1 bg-[#161616] rounded-2xl p-5 border border-white/5 overflow-hidden flex flex-col" data-testid="archive-section">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                    <Archive className="w-5 h-5 text-[#1E6F7A]" />
                    Arkiverede handlingspunkter
                  </h2>

                  {actionItems.filter(a => a.completed).length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Archive className="w-10 h-10 text-white/20 mb-3" />
                      <p className="text-white/40 text-sm">
                        Ingen arkiverede handlingspunkter endnu.
                      </p>
                      <p className="text-white/30 text-xs mt-1">
                        Handlingspunkter arkiveres automatisk når de markeres som færdige under et møde.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 overflow-y-auto flex-1">
                      {actionItems.filter(a => a.completed).sort((a, b) => {
                        const dateA = a.completed_at ? new Date(a.completed_at) : new Date(0);
                        const dateB = b.completed_at ? new Date(b.completed_at) : new Date(0);
                        return dateB - dateA;
                      }).map((item) => {
                        const comments = actionComments[item.id] || [];
                        const isExpanded = expandedActionId === item.id;
                        
                        return (
                          <div key={item.id} className="bg-[#1e1e1e] border border-white/5 rounded-xl overflow-hidden" data-testid={`archive-item-${item.id}`}>
                            <div className="p-4">
                              <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-white/70 text-sm line-through decoration-white/30">{item.description}</p>
                                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
                                    <span className="text-white/40">{item.responsible}</span>
                                    {item.deadline && (
                                      <span className="text-white/30">
                                        Deadline: {new Date(item.deadline).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}
                                      </span>
                                    )}
                                    {item.completed_at && (
                                      <span className="text-green-500/70 flex items-center gap-1">
                                        <Check className="w-3 h-3" />
                                        Afsluttet {new Date(item.completed_at).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}
                                      </span>
                                    )}
                                    <button
                                      onClick={() => toggleActionExpand(item.id)}
                                      className="flex items-center gap-1 text-white/40 hover:text-[#1E6F7A] transition-colors"
                                      data-testid={`archive-comments-${item.id}`}
                                    >
                                      <MessageCircle className="w-3 h-3" />
                                      <span>{comments.length || ''}</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Kommentar-tråd for arkiverede items */}
                            {isExpanded && (
                              <div className="border-t border-white/10 p-3 bg-[#1a1a1a] rounded-b-xl">
                                {loadingComments ? (
                                  <div className="flex justify-center py-2">
                                    <Loader2 className="w-4 h-4 animate-spin text-white/40" />
                                  </div>
                                ) : (
                                  <>
                                    {comments.length > 0 ? (
                                      <div className="space-y-2 max-h-40 overflow-y-auto">
                                        {comments.map((comment) => (
                                          <div key={comment.id} className="flex gap-2">
                                            <div className="w-6 h-6 rounded-full bg-[#1E6F7A]/30 flex items-center justify-center flex-shrink-0">
                                              <span className="text-[10px] text-[#1E6F7A] font-medium">
                                                {comment.author?.charAt(0).toUpperCase()}
                                              </span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-2">
                                                <span className="text-xs text-white/70 font-medium">{comment.author}</span>
                                                <span className="text-[10px] text-white/30">
                                                  {new Date(comment.created_at).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                              </div>
                                              <p className="text-xs text-white/60">{comment.content}</p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-xs text-white/30 text-center py-2">Ingen kommentarer</p>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              ) : (
                /* Tidligere møder tab */
                <section className="flex-1 bg-[#161616] rounded-2xl p-5 border border-white/5 overflow-hidden flex flex-col">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                    <Calendar className="w-5 h-5 text-[#1E6F7A]" />
                    Tidligere møder
                  </h2>

                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-white/40" />
                    </div>
                  ) : meetings.length === 0 ? (
                    <p className="text-white/40 text-sm text-center py-8">
                      Ingen tidligere møder endnu
                    </p>
                  ) : (
                    <div className="space-y-3 overflow-y-auto flex-1">
                      {meetings.map((meeting) => (
                        <div
                          key={meeting.id}
                          className="bg-[#1e1e1e] border border-white/5 rounded-xl overflow-hidden"
                        >
                          <button
                            onClick={() => setExpandedMeeting(expandedMeeting === meeting.id ? null : meeting.id)}
                            className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-[#1E6F7A]" />
                              <div className="text-left">
                                <p className="text-white font-medium">{formatDate(meeting.date)}</p>
                                <p className="text-white/50 text-sm">
                                  {meeting.attendees?.map(a => a.name).join(', ')}
                                </p>
                              </div>
                            </div>
                            {expandedMeeting === meeting.id ? (
                              <ChevronDown className="w-5 h-5 text-white/40" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-white/40" />
                            )}
                          </button>
                          
                          {expandedMeeting === meeting.id && (
                            <div className="px-4 pb-4 border-t border-white/5">
                              <div className="mt-4">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-medium text-white/70">Referat</h4>
                                  <div className="flex items-center gap-3">
                                    <button
                                      onClick={() => copyToClipboard(meeting.summary)}
                                      className="flex items-center gap-1 text-xs text-white/50 hover:text-white/80 transition-colors"
                                    >
                                      <Copy className="w-3 h-3" />
                                      <span>Kopiér</span>
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setDeletingMeetingId(meeting.id); }}
                                      className="flex items-center gap-1 text-xs text-red-400/70 hover:text-red-400 transition-colors"
                                      data-testid={`delete-meeting-btn-${meeting.id}`}
                                    >
                                      <X className="w-3 h-3" />
                                      <span>Slet referat</span>
                                    </button>
                                  </div>
                                </div>
                                <pre className="text-white/80 text-sm whitespace-pre-wrap font-sans bg-[#161616] p-3 rounded-lg max-h-[400px] overflow-y-auto">
                                  {meeting.summary}
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )}
            </>
          ) : (
            /* Aktivt møde med AI chat */
            <section className="flex-1 bg-[#161616] rounded-2xl border border-white/5 flex flex-col overflow-hidden">
              {/* Møde header */}
              <div className="p-4 border-b border-white/10 flex items-center justify-between bg-[#1a1a1a]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1E6F7A] to-[#2a8a97] flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">AI Mødeassistent</h3>
                    <p className="text-white/50 text-xs">
                      {TEAM_MEMBERS.filter(m => attendees.includes(m.id)).map(m => m.name).join(', ')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={endMeetingWithoutSaving}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/50 hover:text-white"
                  title="Afslut uden at gemme"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Chat område */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {meetingNotes.map((note, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 ${note.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {note.type === 'ai' && (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1E6F7A] to-[#2a8a97] flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        note.type === 'user'
                          ? 'bg-[#1E6F7A] text-white'
                          : 'bg-[#252525] text-white/90 border border-white/5'
                      }`}
                    >
                      <pre className="whitespace-pre-wrap font-sans text-sm">{note.content}</pre>
                    </div>
                    {note.type === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                        <UserIcon className="w-4 h-4 text-white/70" />
                      </div>
                    )}
                  </div>
                ))}
                
                {isAiThinking && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1E6F7A] to-[#2a8a97] flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-[#252525] rounded-2xl px-4 py-3 border border-white/5">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-white/50" />
                        <span className="text-white/50 text-sm">Tænker...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={chatEndRef} />
              </div>
              
              {/* Input område */}
              <div className="p-4 border-t border-white/10 bg-[#1a1a1a]">
                {!showFinalSummary ? (
                  <>
                    <div className="flex gap-2 mb-3">
                      <textarea
                        ref={inputRef}
                        value={currentInput}
                        onChange={(e) => setCurrentInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Skriv beslutning, diskussionspunkt eller handling..."
                        className="flex-1 bg-[#252525] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 resize-none focus:outline-none focus:border-[#1E6F7A] transition-colors"
                        rows={2}
                        disabled={isAiThinking}
                        data-testid="meeting-input"
                      />
                      <button
                        onClick={sendMessage}
                        disabled={!currentInput.trim() || isAiThinking}
                        className="px-4 bg-[#1E6F7A] hover:bg-[#257a86] disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
                        data-testid="send-message-btn"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <button
                      onClick={() => setShowMeetingPreview(true)}
                      disabled={meetingNotes.filter(n => n.type === 'user').length === 0 || isAiThinking}
                      className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-xl p-3 flex items-center justify-center gap-2 transition-all font-medium"
                      data-testid="finish-meeting-btn"
                    >
                      <FileText className="w-5 h-5" />
                      Afslut møde og generér referat
                    </button>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-white font-medium">Færdigt referat</h4>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copyToClipboard(finalSummary)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-white/70 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                        >
                          {copied ? (
                            <>
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-green-500">Kopieret!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              <span>Kopiér</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => setShowFinalSummary(false)}
                          className="px-3 py-1.5 text-sm text-white/70 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                        >
                          Tilbage
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-[#252525] border border-white/10 rounded-xl p-4 max-h-[300px] overflow-y-auto">
                      <pre className="text-white/90 text-sm whitespace-pre-wrap font-sans">{finalSummary}</pre>
                    </div>
                    
                    <button
                      onClick={saveMeeting}
                      disabled={isSaving}
                      className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-xl p-4 flex items-center justify-center gap-2 transition-colors font-medium"
                      data-testid="save-meeting-btn"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Gemmer og udtrækker handlingspunkter...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" />
                          <span>Gem møde</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </main>

      {/* Forhåndsvis dagsorden modal */}
      {showAgendaPreview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-2xl border border-white/10 max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#1E6F7A]" />
                Forhåndsvisning af dagsorden
              </h3>
              <button
                onClick={() => setShowAgendaPreview(false)}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {isGeneratingPreview ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-[#1E6F7A]" />
                  <p className="text-white/50 text-sm">Genererer dagsorden...</p>
                </div>
              ) : (
                <pre className="text-white/90 text-sm whitespace-pre-wrap font-sans">
                  {previewAgenda}
                </pre>
              )}
            </div>
            
            <div className="p-4 border-t border-white/10 flex gap-2">
              <button
                onClick={() => setShowAgendaPreview(false)}
                className="flex-1 bg-white/5 hover:bg-white/10 text-white/70 rounded-xl p-3 transition-colors text-sm font-medium"
              >
                Luk
              </button>
              <button
                onClick={() => {
                  setShowAgendaPreview(false);
                  startMeeting();
                }}
                disabled={isGeneratingPreview}
                className="flex-1 bg-gradient-to-r from-[#1E6F7A] to-[#2a8a97] hover:from-[#257a86] hover:to-[#319aa8] text-white rounded-xl p-3 flex items-center justify-center gap-2 transition-all text-sm font-medium"
              >
                <Sparkles className="w-4 h-4" />
                Start møde
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bekræft sletning af referat */}
      {deletingMeetingId && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="delete-meeting-modal">
          <div className="bg-[#161616] border border-red-500/30 rounded-2xl max-w-md w-full p-5 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold mb-1">Slet referat?</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Denne handling kan ikke fortrydes. Der bliver dog automatisk taget en backup inden sletning, så referatet kan gendannes fra dagens backup-fil hvis nødvendigt.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button
                onClick={() => setDeletingMeetingId(null)}
                disabled={isDeletingMeeting}
                className="flex-1 bg-white/5 hover:bg-white/10 text-white/80 rounded-xl p-3 transition-colors text-sm font-medium disabled:opacity-50"
                data-testid="cancel-delete-meeting-btn"
              >
                Annullér
              </button>
              <button
                onClick={() => deleteMeeting(deletingMeetingId)}
                disabled={isDeletingMeeting}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white rounded-xl p-3 flex items-center justify-center gap-2 transition-all text-sm font-medium"
                data-testid="confirm-delete-meeting-btn"
              >
                {isDeletingMeeting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sletter...
                  </>
                ) : (
                  <>
                    <X className="w-4 h-4" />
                    Slet referat permanent
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Meeting preview modal — vises FØR endeligt referat genereres */}
      {showMeetingPreview && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="meeting-preview-modal">
          <div className="bg-[#161616] border border-white/10 rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#1E6F7A]" />
                Læs referat-udkast inden afslutning
              </h3>
              <button
                onClick={() => setShowMeetingPreview(false)}
                className="text-white/40 hover:text-white/80 transition-colors text-xl leading-none"
                data-testid="close-meeting-preview-btn"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Advarsel om udrøftede punkter */}
              {(undiscussedFixed.length > 0 || undiscussedAgenda.length > 0) && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="undiscussed-warning">
                  <div className="flex items-start gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-amber-300 font-medium text-sm">
                        Vi har {undiscussedFixed.length + undiscussedAgenda.length} punkt
                        {undiscussedFixed.length + undiscussedAgenda.length === 1 ? '' : 'er'} der ikke ser ud til at være drøftet
                      </p>
                      <p className="text-amber-200/60 text-xs mt-1">Tag stilling til hvert punkt før du afslutter mødet.</p>
                    </div>
                  </div>
                  {undiscussedFixed.length > 0 && (
                    <div className="mt-3 ml-7">
                      <p className="text-[11px] uppercase tracking-wider text-amber-300/70 mb-1.5 font-semibold">Faste punkter</p>
                      <ul className="space-y-1.5">
                        {undiscussedFixed.map(item => {
                          const isPostponed = postponedItems.has(item.content);
                          return (
                            <li key={item.id} className="flex items-start justify-between gap-3" data-testid={`undiscussed-fixed-${item.id}`}>
                              <span className={`text-sm flex items-start gap-2 flex-1 ${isPostponed ? 'text-white/40 line-through' : 'text-amber-100/90'}`}>
                                <span className="text-amber-400">•</span>
                                <span>{item.content}</span>
                              </span>
                              <button
                                onClick={() => {
                                  const next = new Set(postponedItems);
                                  if (isPostponed) next.delete(item.content); else next.add(item.content);
                                  setPostponedItems(next);
                                }}
                                className={`text-[11px] px-2 py-0.5 rounded-md transition-colors flex-shrink-0 ${
                                  isPostponed
                                    ? 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30'
                                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                                }`}
                                data-testid={`postpone-fixed-${item.id}`}
                              >
                                {isPostponed ? '✓ Udskydes' : 'Udskydes'}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {undiscussedAgenda.length > 0 && (
                    <div className="mt-3 ml-7">
                      <p className="text-[11px] uppercase tracking-wider text-amber-300/70 mb-1.5 font-semibold">Indkomne forslag</p>
                      <ul className="space-y-1.5">
                        {undiscussedAgenda.map(item => {
                          const isPostponed = postponedItems.has(item.content);
                          return (
                            <li key={item.id} className="flex items-start justify-between gap-3" data-testid={`undiscussed-agenda-${item.id}`}>
                              <span className={`text-sm flex items-start gap-2 flex-1 ${isPostponed ? 'text-white/40 line-through' : 'text-amber-100/90'}`}>
                                <span className="text-amber-400">•</span>
                                <span>{item.content}</span>
                              </span>
                              <button
                                onClick={() => {
                                  const next = new Set(postponedItems);
                                  if (isPostponed) next.delete(item.content); else next.add(item.content);
                                  setPostponedItems(next);
                                }}
                                className={`text-[11px] px-2 py-0.5 rounded-md transition-colors flex-shrink-0 ${
                                  isPostponed
                                    ? 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30'
                                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                                }`}
                                data-testid={`postpone-agenda-${item.id}`}
                              >
                                {isPostponed ? '✓ Udskydes' : 'Udskydes'}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  <p className="text-amber-200/50 text-[11px] mt-3 ml-7 italic">
                    Tryk "Udskydes" for at flytte et punkt til næste møde uden at AI'en konkluderer noget om det.
                  </p>
                </div>
              )}

              {(undiscussedFixed.length === 0 && undiscussedAgenda.length === 0) && (fixedPoints.length > 0 || agendaItems.length > 0) && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-center gap-2 text-sm text-emerald-300" data-testid="all-discussed">
                  <CheckCircle className="w-4 h-4" />
                  Alle dagsordenspunkter er drøftet
                </div>
              )}

              {/* Hele mødeforløbet i sammenhæng */}
              <div>
                <h4 className="text-white/80 font-medium text-sm mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-white/40" />
                  Hele mødet i sammenhæng ({meetingNotes.filter(n => n.type === 'user').length} indlæg)
                </h4>
                {meetingNotes.length === 0 ? (
                  <p className="text-white/40 text-sm italic">Ingen mødenoter endnu.</p>
                ) : (
                  <div className="bg-[#0e1015] border border-white/5 rounded-xl p-4 space-y-3 max-h-[40vh] overflow-y-auto" data-testid="meeting-preview-notes">
                    {meetingNotes.map((note, idx) => (
                      <div key={idx} className={`text-sm ${note.type === 'user' ? 'text-white/90' : 'text-[#1E6F7A]/80 italic pl-4 border-l-2 border-[#1E6F7A]/30'}`}>
                        {note.type === 'user' && (
                          <p className="text-[10px] uppercase tracking-wider text-white/30 mb-0.5">{note.author || 'Mødedeltager'}</p>
                        )}
                        {note.type === 'ai' && (
                          <p className="text-[10px] uppercase tracking-wider text-[#1E6F7A]/50 mb-0.5">AI-assistent</p>
                        )}
                        <p className="whitespace-pre-wrap">{note.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-white/10 flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => setShowMeetingPreview(false)}
                className="flex-1 bg-white/5 hover:bg-white/10 text-white/80 rounded-xl p-3 transition-colors text-sm font-medium"
                data-testid="back-to-meeting-btn"
              >
                Tilbage til mødet
              </button>
              <button
                onClick={generateFinalSummary}
                disabled={isAiThinking}
                className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-600 disabled:to-gray-700 text-white rounded-xl p-3 flex items-center justify-center gap-2 transition-all text-sm font-medium"
                data-testid="confirm-finish-meeting-btn"
              >
                <FileText className="w-4 h-4" />
                {(undiscussedFixed.length + undiscussedAgenda.length) > 0
                  ? 'Afslut alligevel og generér referat'
                  : 'Generér endeligt referat'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
